import React, { useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { FaArrowLeft, FaArrowRight, FaChevronLeft } from 'react-icons/fa'
import OTPInput from 'react-otp-input'
import { Link } from 'react-router-dom'
import { mobileOTPSignup, mobileOTPSignUpVerify } from '../../api/auth'
import { Button } from 'flowbite-react'
import FormModal from '../FormModal/FormModal'
import { handleVerifyResponse, showExistingAccountHint } from '../../utils/landingAuth'
import AccountHintBanner from '../AccountHintBanner/AccountHintBanner'
import FieldError, { ButtonSpinner } from '../FieldError/FieldError'
import { getApiErrorMessage, validateLandingMobile } from '../../utils/landingForm'
import "../OtpModal/OtpModal.css"
import "./Drawer.css"

const Drawer = ({ theme }) => {
    const [isDrawer, setIsDrawer] = useState(false);
    const [isVerify, setIsVerify] = useState(false);
    const [mobile, setMobile] = useState('');
    const [openModal, setOpenModal] = useState(false);
    const [otp, setOtp] = useState('');
    const [accountHint, setAccountHint] = useState(null);
    const [mobileError, setMobileError] = useState('');
    const [otpError, setOtpError] = useState('');
    const [sending, setSending] = useState(false);
    const [verifying, setVerifying] = useState(false);
    const mobileInputRef = useRef(null);

    const toggleModal = () => {
        setOpenModal(!openModal)
    }

    const mobileSend = async () => {
        const validationError = validateLandingMobile(mobile);
        if (validationError) {
            setMobileError(validationError);
            return;
        }

        setSending(true);
        setMobileError('');
        try {
            const mobileStatus = await mobileOTPSignup({ mobile, visitor: true });
            if (mobileStatus?.status >= 400) {
                setMobileError(getApiErrorMessage(mobileStatus, 'Could not send OTP'));
                return;
            }
            const hint = showExistingAccountHint(mobileStatus);
            setAccountHint(hint);
            if (hint) {
                toast(hint, { icon: 'ℹ️' });
            } else {
                toast.success('OTP sent successfully');
            }
            setIsVerify(true);
            setOtpError('');
        } catch (err) {
            setMobileError(getApiErrorMessage(err, 'Could not send OTP. Please try again.'));
        } finally {
            setSending(false);
        }
    };

    const otpVerify = async () => {
        if (!otp || otp.length !== 6) {
            setOtpError('Please enter the 6-digit OTP.');
            return;
        }
        setVerifying(true);
        setOtpError('');
        try {
            const payload = {
                mobile,
                otp,
                visitor: true,
                device_id: 'landing-web',
                ip: 'landing-page',
            }
            const updateApi = await mobileOTPSignUpVerify(payload)
            const outcome = handleVerifyResponse(updateApi, mobile, {
                onCloseModals: ({ clearInputs = true } = {}) => {
                    setOtp('');
                    setOtpError('');
                    setMobileError('');
                    setIsVerify(false);
                    setVerifying(false);
                    setSending(false);
                    if (clearInputs) {
                        setMobile('');
                        setAccountHint(null);
                        setIsDrawer(false);
                    }
                },
                onShowRegistrationForm: () => setOpenModal(true),
            })

            if (!outcome.ok) {
                setOtpError(outcome.message || 'OTP verification failed')
                return
            }

            toast.success(outcome.message || 'OTP Verified Successfully')
        } catch (err) {
            setOtpError(getApiErrorMessage(err, 'OTP verification failed. Please try again.'));
        } finally {
            setVerifying(false);
        }
    }

    useEffect(() => {
        if (isDrawer && mobileInputRef.current && !isVerify) {
            mobileInputRef.current.focus();
        }
    }, [isDrawer, isVerify]);

    useEffect(() => {
        if (isVerify) {
            const timer = setTimeout(() => {
                const otpInputElements = document.querySelectorAll('.custom-otp-field2');
                if (otpInputElements.length > 0) {
                    otpInputElements[0].focus();
                }
            }, 200);

            return () => clearTimeout(timer);
        }
    }, [isVerify]);

    const busy = sending || verifying;

    return (
        <>
            <div className={`fixed right-0 top-[20%] z-50 transition-transform duration-500  font-poppins ${isDrawer ? 'translate-x-0' : 'translate-x-[295px]'}`}>
                <div className={`w-[330px] min-h-[240px] h-auto flex flex-col justify-center items-center relative px-5 py-3  border-[1px] border-dotted  shadow-xl border-r-0 rounded-l-lg ${theme === "dark" ? "border-white bg-black/70 backdrop-blur-lg" : "border-slate-400 bg-white/80 backdrop-blur-lg"}`}>
                    {!isDrawer && (
                        <div className={`flex flex-col absolute top-[50%] left-[15px] transform translate-y-[-50%] font-bold ${theme === "dark" ? "text-white" : "text-[#13224d]"}`}>
                            <p className='text-xs'>G</p>
                            <p className='text-xs'>E</p>
                            <p className='text-xs mb-1'>T</p>
                            <p className='text-xs'>S</p>
                            <p className='text-xs'>T</p>
                            <p className='text-xs'>A</p>
                            <p className='text-xs'>R</p>
                            <p className='text-xs mb-1'>T</p>
                            <p className='text-xs '>F</p>
                            <p className='text-xs'>R</p>
                            <p className='text-xs'>E</p>
                            <p className='text-xs'>E</p>
                        </div>
                    )}

                    <div className={`${!isDrawer ? "hidden" : "block"} w-full`}>
                        <h2 className={`${theme === "dark" ? "text-white" : "text-[#13224d] "} text-xl font-bold`}>GET START FREE</h2>
                        <div>
                            <p className={`text-xs font-medium ${theme === "dark" ? "text-[#E3E5ED]" : ""} pt-2`}>From lead CRM and proposal management to inventory and vendor management, our platform offers everything you need to ensure your projects run smoothly and efficiently.</p>
                            {isVerify ? (
                                <>
                                    <AccountHintBanner
                                        message={accountHint}
                                        dark={theme === 'dark'}
                                        className="mt-2"
                                    />
                                    <div className='flex flex-col items-center mt-3'>
                                        <OTPInput
                                            value={otp}
                                            onChange={(value) => {
                                                setOtp(value);
                                                if (otpError) setOtpError('');
                                            }}
                                            inputType="tel"
                                            numInputs={6}
                                            inputStyle={`custom-otp-field2${otpError ? ' otp-field-error' : ''}`}
                                            renderInput={(props) => (
                                                <input {...props} disabled={busy} aria-invalid={Boolean(otpError)} />
                                            )}
                                        />
                                        <FieldError
                                            message={otpError}
                                            className={`w-full ${theme === 'dark' ? 'text-red-300' : ''}`}
                                        />
                                    </div>
                                </>
                            ) : (
                                <div className="mt-5">
                                    <input
                                        ref={mobileInputRef}
                                        value={mobile}
                                        onChange={(e) => {
                                            setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))
                                            setAccountHint(null)
                                            if (mobileError) setMobileError('')
                                        }}
                                        className={`custom-number-input border-[1px] border-dotted w-full rounded-lg ${
                                            mobileError ? 'border-red-500 ring-1 ring-red-200' : 'border-slate-400'
                                        }`}
                                        type="text"
                                        inputMode="numeric"
                                        maxLength={10}
                                        placeholder='Enter Your Mobile'
                                        disabled={sending}
                                        aria-invalid={Boolean(mobileError)}
                                    />
                                    <FieldError
                                        message={mobileError}
                                        className={theme === 'dark' ? 'text-red-300' : ''}
                                    />
                                </div>
                            )}
                            <div className='flex justify-between items-center mt-3'>
                                <Link to={'/schedule-demo'}>
                                    <span onClick={() => setIsDrawer(false)} className={`text-sm underline  cursor-pointer ${theme === "dark" ? "text-[#E3E5ED] hover:text-white" : ""}`}>Want Demo ?</span>
                                </Link>
                                {isVerify ? (
                                    <div className='flex items-center'>
                                        <span
                                            onClick={() => {
                                                if (busy) return;
                                                setIsVerify(false)
                                                setOtp('')
                                                setOtpError('')
                                                setAccountHint(null)
                                            }}
                                            className={`me-2 ${busy ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                                        >
                                            <FaChevronLeft size={15} />
                                        </span>
                                        <Button
                                            className='bg-primaryColor'
                                            color="dark"
                                            size='sm'
                                            disabled={busy || !otp || otp.length !== 6}
                                            onClick={otpVerify}
                                        >
                                            {verifying ? (
                                                <span className="inline-flex items-center gap-2">
                                                    <ButtonSpinner /> Verifying…
                                                </span>
                                            ) : (
                                                'Verify OTP'
                                            )}
                                        </Button>
                                    </div>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={mobileSend}
                                        disabled={sending}
                                        className='bg-primaryColor w-fit p-3 px-3 text-white text-xs rounded-md disabled:opacity-70 inline-flex items-center gap-2'
                                    >
                                        {sending ? (
                                            <>
                                                <ButtonSpinner /> Sending…
                                            </>
                                        ) : (
                                            'Start Now'
                                        )}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>

                    <div onClick={() => setIsDrawer(!isDrawer)} className={` cursor-pointer h-9 w-9 absolute top-[50%] -left-7 transform translate-y-[-50%] rounded-md flex items-center justify-center ${theme === "dark" ? "bg-slate-500 hover:bg-slate-400" : "bg-[#13224d] hover:bg-[#13224de6]"}`}>
                        <span>{isDrawer ? <FaArrowRight size={20} className="text-white" /> : <span className='bounce-arrow'><FaArrowLeft size={20} className='text-white' /></span>}</span>
                    </div>
                </div>
            </div>

            <FormModal
                isModal={openModal}
                toggle={toggleModal}
                mobile={mobile}
                onHandoffComplete={() => {
                    setOpenModal(false);
                    setMobile('');
                    setOtp('');
                    setAccountHint(null);
                    setMobileError('');
                    setOtpError('');
                    setIsVerify(false);
                    setIsDrawer(false);
                }}
            />
        </>
    )
}

export default Drawer
